/**
 * Error Boundary
 *
 * Catches and handles React errors in the application.
 * Prevents the entire app from crashing when an error occurs.
 */

'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import posthog from 'posthog-js';
import { logger } from '@/lib/logger';
import { attemptChunkReload, isChunkLoadError } from '@/lib/chunk-reload';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    // Log error to console
    logger.error('Application error:', error);

    // Capture to PostHog error tracking
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.captureException(error, {
        $exception_source: 'react_error_boundary',
        $exception_type: error.name,
        digest: error.digest,
      });
    }
  }, [error]);

  // gh #825: a stale-build chunk 404 cannot be fixed by reset() — only a hard
  // document reload picks up the new build. Runs in its own effect so the
  // PostHog capture above is always issued first (the reload tears the page
  // down). Guarded against reload loops; see lib/chunk-reload.ts.
  useEffect(() => {
    attemptChunkReload(error);
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong!</h2>
          <p className="text-gray-600 mb-6">
            {chunkError
              ? 'The app was updated while this page was open. Reloading picks up the new version.'
              : 'An error occurred while loading this page. Please try again.'}
          </p>

          {error.message && (
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-6 text-left">
              <p className="text-sm text-red-800 font-mono">{error.message}</p>
            </div>
          )}

          {/* gh #825: reset() re-runs the same dead bundle on a chunk error, so
              the manual affordance must be a hard reload too — this is the path
              the user takes when the auto-reload was suppressed by the loop
              guard. */}
          <button
            onClick={chunkError ? () => window.location.reload() : reset}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            {chunkError ? 'Reload page' : 'Try again'}
          </button>

          <div className="mt-4">
            <Link
              href="/"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Return to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
